/**
 * Which host serves the admin portal, and how to link back to the user app from it.
 *
 * The portal lives on an `admin`-family first label of the app host: `admin.` in prod
 * (`admin.gozig.com` app-admin, `admin.kuder.io` platform-admin) and `admin.localhost` in
 * development; the labs generation split adds `admin-app.` / `admin-plat.` (+ their `-dv`
 * concretes) because app-admin and platform-admin share the single `labs.kuder.io` apex.
 *
 * This is only a UI-routing hint. The API's own `ADMIN_HOSTS` check is the security boundary;
 * a visitor who forces the portal to render on the wrong host still gets 404s from every
 * admin endpoint.
 *
 * Interim: a compiled label set. ASA-1607 (SSR) is expected to move this decision server-side,
 * where the request host + the `ADMIN_HOSTS` env are a single source of truth with no client
 * hardcode; until then keep this list in step with the FD `admin-*` domains + `ADMIN_HOSTS`.
 */

/**
 * Whole first-labels (not a prefix) that serve an admin portal, so user hosts like
 * `admin-tools.gozig.com` and `notadmin.gozig.com` stay user hosts. Add `admin-plat` /
 * `admin-plat-dv` when platform-admin (PLAT-176) exists.
 */
const ADMIN_FIRST_LABELS = new Set<string>([
    'admin', // prod app-admin (admin.gozig.com), prod platform-admin (admin.kuder.io), admin.localhost
    'admin-app', // labs app-admin vanity (admin-app.labs.kuder.io)
    'admin-app-dv', // labs app-admin concrete (admin-app-dv.labs.kuder.io)
    'admin-app-stg', // non-prod app-admin concrete (admin-app-stg.np.kuder.io; admin-app.np.kuder.io is the vanity)
    'admin-app-dev-engineer-6-asa', // DE6's app-admin (admin-app-dev-engineer-6-asa.labs.kuder.io)
]);

/** `example.com:8080` -> `example.com`. `window.location.hostname` has no port; `host` does. */
const stripPort = (host: string): string => {
    const colon = host.indexOf(':');
    return colon === -1 ? host : host.slice(0, colon);
};

const normalize = (host: string): string => stripPort(host.trim().toLowerCase());

export const isAdminHost = (host: string = typeof window === 'undefined' ? '' : window.location.host): boolean => {
    const labels = normalize(host).split('.');
    // Require a real FQDN (a label after the admin label), so a bare `admin` is not an admin host.
    // SSR-safe default (matches ASA-1607): with no `window` the host is '' -> not an admin host.
    return labels.length > 1 && ADMIN_FIRST_LABELS.has(labels[0]);
};

/**
 * Origin of the user app as seen from the admin portal, for links back to it. Derived by dropping
 * the admin-family first label — `admin.gozig.com` -> `gozig.com`. On the shared labs apex this
 * yields the apex (`admin-app.labs.kuder.io` -> `labs.kuder.io`) rather than a specific app host,
 * so back-links there are best-effort until SSR (ASA-1607) supplies the real user-app origin.
 */
export const userAppOrigin = (location: Location = window.location, configuredOrigin?: string): string => {
    // Prefer an explicit, env-configured user-app origin (dv.labs.kuder.io / stg.np.kuder.io /
    // gozig.com), surfaced to the client via /api/config/public (USER_APP_ORIGIN). The label-drop
    // below is the fallback (prod-exact `admin.gozig.com` -> `gozig.com`; best-effort on the shared
    // labs apex) so local/unconfigured still works.
    const configured = configuredOrigin?.trim();
    if (configured) {
        return configured.replace(/\/+$/, '');
    }
    const host = location.host;
    if (!isAdminHost(host)) {
        return location.origin;
    }
    const firstDot = host.indexOf('.');
    return firstDot === -1 ? location.origin : `${location.protocol}//${host.slice(firstDot + 1)}`;
};
